import type { CalendarPeriod } from '@ui/components/calendar/picker'
import {
  fromInventoryCalendarDate,
  isInventoryPeriodValid,
  toInventoryCalendarDate,
} from '@booking/utils/inventoryPeriod'
import { useIntervalFn, useNow } from '@vueuse/core'
import { setHours, setMinutes, startOfDay } from 'date-fns'

export function useInventoryPeriodLimits(timezone: () => string) {
  const now = useNow({ scheduler: cb => useIntervalFn(cb, 1000) })
  const today = computed(() => startOfDay(toInventoryCalendarDate(now.value, timezone())))

  function disabledDate(date: Date) {
    return startOfDay(date) < today.value
  }

  function toInstantPeriod(value: CalendarPeriod): CalendarPeriod {
    return {
      start: fromInventoryCalendarDate(value.start, timezone()),
      end: fromInventoryCalendarDate(value.end, timezone()),
    }
  }

  function isValid(value: CalendarPeriod, currentTime = now.value) {
    return isInventoryPeriodValid(toInstantPeriod(value), currentTime)
  }

  function getTimeOptions(date: Date, after?: Date) {
    return Array.from({ length: 48 }, (_, index) => {
      const hours = Math.floor(index / 2)
      const minutes = index % 2 ? 30 : 0
      return { id: index * 30, label: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}` }
    }).filter(({ id }) => {
      const local = setMinutes(setHours(startOfDay(date), Math.floor(id / 60)), id % 60)
      const instant = fromInventoryCalendarDate(local, timezone())
      return instant >= now.value && (!after || instant > fromInventoryCalendarDate(after, timezone()))
    })
  }

  return { now, today, disabledDate, toInstantPeriod, isValid, getTimeOptions }
}
